import { getDataProvider, OrderJson } from "@smeetz/orders";
import Dexie from "dexie";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import Timeout from "await-timeout";
import { dexieDB } from "@/services/dexie-database";
import { IPendingOrder } from "@/types/db/order-sync";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { classifySyncError, SyncErrorKind } from "@/db/sync-error";
import { LogLevel, LogService } from "@/services/logging";
import { initiateOrderSync, MAX_SYNC_ATTEMPTS } from "@/services/sync-service";

/**
 * Write-ahead enqueue of a completed order.
 *
 * Stores the order JSON as Pending BEFORE any network attempt so the sale is
 * durable even if persistence never succeeds, then kicks off the background
 * drain. Keyed by order id, so re-completing an edited order overwrites any
 * still-pending row for that order with the newer full-state snapshot.
 */
export async function saveOrderForSync(order: OrderJson): Promise<void> {
  if (!order || !order.id) {
    LogService.info(
      `Error: Attempted to queue an order without an id`,
      { order },
      LogLevel.Error
    );
    return;
  }

  const pendingOrder: IPendingOrder = {
    _id: String(order.id),
    reference: String(order.reference),
    order: cloneDeep(order),
    syncStatus: SyncStatus.Pending,
    attempts: 0,
    creationDateTime: dayjs().toISOString(),
  };

  try {
    // Version the row atomically with the write: a snapshot enqueued while an
    // older one is mid-POST must not be deleted/re-statused by that older
    // attempt's outcome (see the drain's version guards).
    await dexieDB.transaction("rw", dexieDB.orders, async () => {
      const existing = await dexieDB.orders.get(pendingOrder._id);
      pendingOrder.version = (existing?.version ?? 0) + 1;
      await dexieDB.orders.put(pendingOrder);
    });
  } catch (err: any) {
    LogService.info(
      `Error when storing pending order ${pendingOrder.reference} into local db`,
      { params: { orderId: pendingOrder._id, order: pendingOrder, err } },
      LogLevel.Error
    );
    throw err;
  }

  // The row payload never mutates after enqueue (later enqueues replace the
  // whole row with a higher version), so this log holds the exact payload of
  // every POST attempt for this version — recoverable by order id, which this
  // message keeps for that reason.
  LogService.info(
    `(compressed) Queueing order ${pendingOrder.reference} for sync, id: ${pendingOrder._id}, version: ${pendingOrder.version}`,
    pendingOrder
  );

  // Trigger the background drain
  initiateOrderSync();
}

/**
 * Record the outcome of a POST attempt on the row it was made for. Guarded by
 * `version`: if a newer snapshot was enqueued while this one was on the wire,
 * the newer row is left to sync fresh (re-marked Pending in case this drain
 * pass had flipped it InProgress) instead of inheriting the old attempt's
 * status — an old payload's rejection must never mark a newer payload Ignored.
 */
async function applySyncOutcome(
  posted: IPendingOrder,
  changes: { syncStatus: SyncStatus; attempts?: number }
): Promise<void> {
  await dexieDB.transaction("rw", dexieDB.orders, async () => {
    const current = await dexieDB.orders.get(posted._id);
    if (!current) {
      return;
    }
    if (current.version !== posted.version) {
      LogService.info(
        `Sync: order ${posted.reference} was re-enqueued (version ${current.version}) while version ${posted.version} was syncing, keeping the newer snapshot Pending`,
        { orderId: posted._id }
      );
      await dexieDB.orders.update(posted._id, {
        syncStatus: SyncStatus.Pending,
      });
      return;
    }
    await dexieDB.orders.update(posted._id, changes);
  });
}

/**
 * Persist a single queued order to the backend via the order data provider.
 * `persistOrder` is a full-state upsert keyed by order id, so it is safe to retry.
 */
async function syncPendingOrder(pendingOrder: IPendingOrder): Promise<void> {
  try {
    // The payload itself is journaled (compressed) at enqueue for this same
    // version — this line ties the POST attempt to that exact snapshot.
    LogService.info(
      `Sync: Persisting order ${pendingOrder.reference} to backend, version: ${pendingOrder.version}, attempts so far: ${pendingOrder.attempts}`,
      {
        orderId: pendingOrder._id,
        sessionState: pendingOrder.order.orderSession?.state,
        sessionId: pendingOrder.order.orderSession?.id,
        paymentCount: pendingOrder.order.orderSession?.payments?.length,
        previousSessionCount: pendingOrder.order.previousSessions?.length,
      }
    );
    await getDataProvider().persistOrder(pendingOrder.order);

    // Success — delete-on-success keeps the queue small. Version-guarded: if
    // a newer snapshot was enqueued while this one was on the wire (e.g. the
    // completed order right after its payment snapshot), deleting blindly
    // would drop that newer snapshot and the backend would keep the stale
    // state forever.
    await dexieDB.transaction("rw", dexieDB.orders, async () => {
      const current = await dexieDB.orders.get(pendingOrder._id);
      if (!current) {
        return;
      }
      if (current.version !== pendingOrder.version) {
        LogService.info(
          `Sync: order ${pendingOrder.reference} was re-enqueued (version ${current.version}) while version ${pendingOrder.version} was syncing, keeping the newer snapshot Pending`,
          { orderId: pendingOrder._id }
        );
        await dexieDB.orders.update(pendingOrder._id, {
          syncStatus: SyncStatus.Pending,
        });
        return;
      }
      await dexieDB.orders.delete(pendingOrder._id);
    });
    LogService.info(
      `Sync: Successfully persisted order ${pendingOrder.reference}, version ${pendingOrder.version}`,
      { orderId: pendingOrder._id }
    );
  } catch (error: any) {
    switch (classifySyncError(error)) {
      case SyncErrorKind.Network:
        // Transient — mark Failed WITHOUT incrementing attempts so it retries
        // forever once the connection is back.
        LogService.info(
          `Network error when persisting order ${pendingOrder.reference}, will retry`,
          { orderId: pendingOrder._id, error },
          LogLevel.Warning
        );
        await applySyncOutcome(pendingOrder, {
          syncStatus: SyncStatus.Failed,
        });
        break;

      case SyncErrorKind.Validation:
        // The backend rejects this payload — retrying the identical snapshot
        // can never succeed. Mark Ignored and stop; the 5-min queue monitor
        // surfaces Ignored rows for investigation.
        LogService.info(
          `Validation error when persisting order ${pendingOrder.reference}, marking Ignored (will not retry)`,
          { orderId: pendingOrder._id, error },
          LogLevel.Error
        );
        await applySyncOutcome(pendingOrder, {
          syncStatus: SyncStatus.Ignored,
          attempts: (pendingOrder.attempts || 0) + 1,
        });
        break;

      case SyncErrorKind.Backend: {
        // Other backend failure (5xx, auth, rate limit) — retry within the
        // MAX_SYNC_ATTEMPTS budget. Retries the drain still owns are
        // journaled as WARNING; only exhausting the budget ships as ERROR,
        // so one stuck order raises one alert instead of one per retry.
        const attempts = (pendingOrder.attempts || 0) + 1;
        const exhausted = attempts >= MAX_SYNC_ATTEMPTS;
        LogService.info(
          exhausted
            ? `Error when persisting order ${pendingOrder.reference}: retry budget exhausted after ${attempts} attempts`
            : `Sync: persisting order ${pendingOrder.reference} failed (attempt ${attempts}/${MAX_SYNC_ATTEMPTS}), will retry`,
          { orderId: pendingOrder._id, error },
          exhausted ? LogLevel.Error : LogLevel.Warning
        );
        await applySyncOutcome(pendingOrder, {
          syncStatus: SyncStatus.Failed,
          attempts,
        });
        break;
      }
    }
  }
}

/**
 * Drain queued orders from Dexie to the backend.
 * Priority is given to new (Pending) orders over Failed ones.
 */
export async function syncDexieOrders(limit = 10): Promise<void> {
  let syncedCount = 0;

  while (syncedCount < limit) {
    if (!dexieDB.isOpen()) {
      LogService.info("Database connection closed during sync, stopping");
      break;
    }

    let orderToSync: IPendingOrder | undefined;

    try {
      // Newest Pending order first (via [syncStatus+creationDateTime] index).
      const newestPending = await dexieDB.orders
        .where("[syncStatus+creationDateTime]")
        .between(
          [SyncStatus.Pending, Dexie.minKey],
          [SyncStatus.Pending, Dexie.maxKey]
        )
        .reverse()
        .first();

      if (newestPending) {
        orderToSync = newestPending;
      } else {
        // No Pending — pick a Failed order with the fewest attempts.
        const failed = await dexieDB.orders
          .where("[syncStatus+attempts]")
          .between(
            [SyncStatus.Failed, 0],
            [SyncStatus.Failed, MAX_SYNC_ATTEMPTS]
          )
          .first();
        if (failed) {
          orderToSync = failed;
        }
      }
    } catch (error) {
      LogService.info(
        `Sync: Error when fetching pending order from Dexie db`,
        { error },
        LogLevel.Error
      );
      break;
    }

    if (!orderToSync) {
      LogService.info("Sync: No more orders to sync");
      break;
    }

    // Mark InProgress before syncing (reset to Pending on startup if
    // interrupted). Field-level update + re-read: a newer snapshot may have
    // been enqueued between the query above and this mark, and it must not be
    // overwritten by this stale row object — post the freshest one instead.
    try {
      await dexieDB.orders.update(orderToSync._id, {
        syncStatus: SyncStatus.InProgress,
      });
      orderToSync = (await dexieDB.orders.get(orderToSync._id)) ?? orderToSync;
    } catch (error) {
      LogService.info(
        `Sync: Error marking order ${orderToSync.reference} as InProgress`,
        { orderId: orderToSync._id, error },
        LogLevel.Error
      );
    }

    await syncPendingOrder(orderToSync);

    syncedCount++;
    await Timeout.set(1000);
  }
}

/**
 * Count orders still awaiting sync (Pending + Failed within retry budget).
 */
export async function unsyncedOrdersCount({
  includeInProgress = false,
}: {
  // A persist in flight is not on the backend yet, which matters to the cashfloat close.
  includeInProgress?: boolean;
} = {}): Promise<number> {
  const [pendingCount, inProgressCount, failedCount] = await Promise.all([
    dexieDB.orders.where("syncStatus").equals(SyncStatus.Pending).count(),
    includeInProgress
      ? dexieDB.orders.where("syncStatus").equals(SyncStatus.InProgress).count()
      : 0,
    dexieDB.orders
      .where("[syncStatus+attempts]")
      .between([SyncStatus.Failed, 0], [SyncStatus.Failed, MAX_SYNC_ATTEMPTS])
      .count(),
  ]);

  return pendingCount + inProgressCount + failedCount;
}
